import { ClockIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react'
import type { FormField, FormVariant } from '../types/form'

const businessTypes = [
  'Retail',
  'Food & Beverage',
  'Manufacturing',
  'Services',
  'Hospitality',
  'Healthcare',
  'Other',
]

const fullName: FormField = {
  type: 'text',
  name: 'fullName',
  label: 'Full name',
  required: true,
  autoComplete: 'name',
  placeholder: 'e.g. Aline Uwimana',
  half: true,
}

const businessName: FormField = {
  type: 'text',
  name: 'businessName',
  label: 'Business name',
  required: true,
  autoComplete: 'organization',
  placeholder: 'e.g. Inzozi Mini Market',
  half: true,
}

const businessType: FormField = {
  type: 'select',
  name: 'businessType',
  label: 'Business type',
  required: true,
  options: businessTypes,
  placeholder: 'Select your business type',
}

export const formVariants: Record<string, FormVariant> = {
  demo: {
    id: 'demo',
    path: '/demo',
    redirectTo: '/demo/thanks',
    eyebrow: 'Book a demo',
    headline: 'See Kayko in action',
    subheadline: 'Get a 30-minute walkthrough tailored to your business.',
    points: [
      'A product specialist will contact you within 1 business day',
      'We’ll show the tools that fit your business',
      'No commitment',
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/uploads/u85ioFqg7LTMidgQDirfqD/ChatGPT_Image_Sep_24,_2026,_12_52_14_PM.png',
      alt: 'Kayko dashboard on a tablet showing revenue, cash balance, and best-selling products',
    },
    testimonial: {
      quote: 'The demo showed us exactly how Kayko would work in our café. We were selling on it the same week.',
      name: 'Jean-Paul Niyonzima',
      business: 'Mizero Café & Bakery',
      image: 'https://cdn.magicpatterns.com/patterns/generated-images/2c23e8d6-e530-4eee-9f47-5e53b680c437.jpg',
    },
    form: {
      title: 'Book your demo',
      subtitle: 'Takes about a minute.',
      submitLabel: 'Book my demo',
      loadingLabel: 'Booking your demo…',
      fields: [
        fullName,
        businessName,
        { type: 'tel', name: 'phone', label: 'Phone number', required: true, helper: 'We’ll use this to schedule your demo.' },
        {
          type: 'email',
          name: 'email',
          label: 'Email',
          placeholder: 'name@business.rw',
          requiredIf: { field: 'contactMethod', equals: 'Email' },
        },
        businessType,
        { type: 'choice', name: 'locations', label: 'Number of locations', required: true, options: ['1', '2–5', '6+'], half: true },
        {
          type: 'select',
          name: 'staff',
          label: 'Number of staff',
          required: true,
          options: ['1', '2–5', '6–20', '21–100', '100+'],
          placeholder: 'Select',
          half: true,
        },
        {
          type: 'choice',
          name: 'contactMethod',
          label: 'Preferred contact method',
          required: true,
          options: ['Phone call', 'WhatsApp', 'Email'],
        },
        {
          type: 'textarea',
          name: 'interests',
          label: 'What would you like to see?',
          placeholder: 'e.g. Taking mobile money payments and tracking stock across two shops',
          rows: 3,
        },
      ],
    },
    success: {
      headline: 'Your demo request is in',
      body: 'We’ll contact you within 1 business day to schedule it.',
      steps: [
        'A product specialist reviews your details',
        'We contact you by your preferred method to pick a time',
        'You get a 30-minute walkthrough tailored to your business',
      ],
    },
    filledSample: {
      fullName: 'Aline Uwimana',
      businessName: 'Inzozi Mini Market',
      phone: '788 412 950',
      phone__cc: 'RW',
      email: 'aline@inzozi.rw',
      businessType: 'Retail',
      locations: '2–5',
      staff: '6–20',
      contactMethod: 'WhatsApp',
      interests: 'Mobile money payments and tracking stock across our Remera and Kimironko shops.',
    },
    errorSample: {
      fullName: 'Aline Uwimana',
      businessName: '',
      phone: '788 41',
      phone__cc: 'RW',
      email: 'aline@inzozi',
      businessType: 'Retail',
      locations: '',
      staff: '',
      contactMethod: 'WhatsApp',
    },
  },

  'get-started': {
    id: 'get-started',
    path: '/get-started',
    eyebrow: 'Get started',
    headline: 'Start running your business on Kayko',
    subheadline: 'Set up your workspace in minutes.',
    points: ['Sales, stock and expenses in one place', 'RRA EBM invoices and receipts', 'Upgrade as you grow'],
    image: {
      src: 'https://cdn.magicpatterns.com/uploads/hrSxrDybaWkqRaXzBR7V3y/ChatGPT_Image_Sep_26,_2026,_04_09_02_PM-1.png',
      alt: 'Shopkeeper arranging products on shelves in a busy retail store',
    },
    testimonial: {
      quote: 'With Kayko, managing my inventory and tracking my sales has become second nature.',
      name: 'Jemima Nyirasafari',
      business: 'All-in-one supermarket',
      image: 'https://cdn.magicpatterns.com/patterns/generated-images/26e15333-45ec-4421-ac04-24cf98518008.jpg',
    },
    form: {
      title: 'Create your account',
      subtitle: 'No card needed to set up.',
      submitLabel: 'Create my account',
      loadingLabel: 'Creating your account…',
      showPlanChip: true,
      fields: [
        fullName,
        businessName,
        { type: 'tel', name: 'phone', label: 'Phone number', required: true },
        { type: 'email', name: 'email', label: 'Email', required: true, placeholder: 'name@business.rw', helper: 'You’ll use this to sign in.' },
        businessType,
        { type: 'password', name: 'password', label: 'Password', required: true, requiredMessage: 'Create a password' },
      ],
      afterSubmit: { text: 'Already have an account?', linkLabel: 'Sign in', href: '#' },
    },
    success: {
      headline: 'Your account is ready',
      body: 'Your Kayko workspace is set up. Add your products and make your first sale today.',
      primary: { label: 'Go to my workspace', href: '#' },
    },
    filledSample: {
      fullName: 'Eric Habimana',
      businessName: 'Habimana Hardware',
      phone: '722 318 604',
      phone__cc: 'RW',
      email: 'eric@habimanahardware.rw',
      businessType: 'Retail',
      password: 'Gikondo2026!',
    },
    errorSample: {
      fullName: 'Eric Habimana',
      businessName: 'Habimana Hardware',
      phone: '0722 31',
      phone__cc: 'RW',
      email: 'eric@habimana',
      businessType: '',
      password: 'kigali',
    },
  },

  contact: {
    id: 'contact',
    path: '/contact',
    redirectTo: '/thanks',
    eyebrow: 'Contact',
    headline: 'Talk to our team',
    subheadline: 'Questions about Kayko, partnerships, or support? We’re here to help.',
    contactCards: [
      { icon: MapPinIcon, label: 'Office', value: '57A KG 215 St, Kigali' },
      { icon: PhoneIcon, label: 'Phone', value: '+250 788 300 400', href: 'tel:+250788300400' },
      { icon: MailIcon, label: 'Email', value: 'hello@kayko.rw', href: 'mailto:hello@kayko.rw' },
      { icon: ClockIcon, label: 'Support hours', value: 'Mon–Sat, 8:00–19:00' },
    ],
    showMap: true,
    form: {
      title: 'Send us a message',
      subtitle: 'We reply within 1 business day.',
      submitLabel: 'Send message',
      loadingLabel: 'Sending…',
      fields: [
        { ...fullName, half: false },
        { type: 'tel', name: 'phone', label: 'Phone number', required: true, half: true },
        { type: 'email', name: 'email', label: 'Email', required: true, placeholder: 'name@business.rw', half: true },
        {
          type: 'select',
          name: 'topic',
          label: 'Topic',
          required: true,
          options: ['Sales', 'Support', 'Partnerships', 'Press', 'Other'],
          placeholder: 'What’s this about?',
        },
        {
          type: 'textarea',
          name: 'message',
          label: 'Message',
          required: true,
          placeholder: 'How can we help?',
          rows: 5,
        },
      ],
    },
    success: {
      headline: 'Message sent',
      body: 'We’ll reply within 1 business day.',
    },
    filledSample: {
      fullName: 'Diane Mukamana',
      phone: '783 915 227',
      phone__cc: 'RW',
      email: 'diane@mukamanapharmacy.rw',
      topic: 'Partnerships',
      message: 'We run three pharmacies in Kigali and Huye and would like to discuss a partnership for our suppliers.',
    },
    errorSample: {
      fullName: 'Diane Mukamana',
      phone: '783 9',
      phone__cc: 'RW',
      email: '',
      topic: '',
      message: '',
    },
  },

  'business-finance': {
    id: 'business-finance',
    path: '/business-finance/apply',
    eyebrow: 'Business Financing',
    headline: 'Get early access to Business Financing',
    subheadline: 'Working capital based on your Kayko business records.',
    points: [
      'Built on the records you already keep in Kayko',
      'Simple application',
      'We’ll contact you when financing opens for your business',
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/patterns/generated-images/d825f78d-1c2e-4866-ad21-178ec126a998.jpg',
      alt: 'Fashion shop owner reviewing her business records in her shop',
    },
    form: {
      title: 'Join early access',
      subtitle: 'Takes about two minutes.',
      submitLabel: 'Join early access',
      loadingLabel: 'Joining…',
      fields: [
        fullName,
        businessName,
        { type: 'tel', name: 'phone', label: 'Phone number', required: true, half: true },
        { type: 'email', name: 'email', label: 'Email', required: true, placeholder: 'name@business.rw', half: true },
        { type: 'choice', name: 'isCustomer', label: 'Are you a Kayko customer?', required: true, options: ['Yes', 'No'] },
        {
          type: 'text',
          name: 'workspace',
          label: 'Workspace email or phone',
          required: true,
          showIf: { field: 'isCustomer', equals: 'Yes' },
          helper: 'The email or phone you use to sign in to Kayko.',
          placeholder: 'name@business.rw or 078…',
        },
        {
          type: 'select',
          name: 'operating',
          label: 'How long has your business been operating?',
          required: true,
          options: ['Less than 6 months', '6–12 months', '1–3 years', '3+ years'],
          placeholder: 'Select',
        },
        { ...businessType, half: true },
        {
          type: 'select',
          name: 'financingUse',
          label: 'What would you use financing for?',
          required: true,
          options: ['Stock purchase', 'Equipment', 'Expansion', 'Cash flow', 'Other'],
          placeholder: 'Select',
          half: true,
        },
        {
          type: 'checkbox',
          name: 'consent',
          label: 'I agree that Kayko and Mula Financial Services Ltd may use my business records to assess eligibility.',
          required: true,
          requiredMessage: 'Please agree so we can assess your eligibility',
        },
      ],
      disclosure: 'Subject to eligibility and approval. Financing provided by Mula Financial Services Ltd.',
    },
    success: {
      headline: 'You’re on the list',
      body: 'We’ll contact you when Business Financing opens for your business.',
    },
    filledSample: {
      fullName: 'Solange Ingabire',
      businessName: 'Ingabire Fashion House',
      phone: '738 204 116',
      phone__cc: 'RW',
      email: 'solange@ingabirefashion.rw',
      isCustomer: 'Yes',
      workspace: 'solange@ingabirefashion.rw',
      operating: '1–3 years',
      businessType: 'Retail',
      financingUse: 'Stock purchase',
      consent: true,
    },
    errorSample: {
      fullName: 'Solange Ingabire',
      businessName: 'Ingabire Fashion House',
      phone: '738 204 116',
      phone__cc: 'RW',
      email: 'solange@ingabire',
      isCustomer: 'Yes',
      workspace: '',
      operating: '',
      businessType: 'Retail',
      financingUse: '',
      consent: false,
    },
  },
}
